
import React, { useState, useEffect } from 'react';
import {Box,Card,CardContent,Typography,Button,TextField,Stack,MenuItem,Alert,CircularProgress,IconButton,Grid,Chip,Accordion,AccordionSummary,AccordionDetails,Paper
} from '@mui/material';
import {ArrowBack,Science,CheckCircle,Add,Delete,ExpandMore,Warning,Biotech
} from '@mui/icons-material';
import { orderAssignmentAPI } from '../deliveryAgentAPI';

const SampleCollectionForm = ({ order, onBack, onSampleCollected, onReportFailure }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Parse tests from order
  const parseTestsFromOrder = (order) => {
    const tests = [];
    
    // Check if order has multiple tests in serviceName
    // Example: "AMH - Anti Mullerian Hormone (₹2000), Amylase - Serum (₹450), Hepatitis B envelope Antibody (AntiHBe) (₹900)"
    if (order.serviceName && order.serviceName.includes(',')) {
      const testStrings = order.serviceName.split(',').map(t => t.trim());
      
      testStrings.forEach(testString => {
        // Extract test name and price
        const match = testString.match(/^(.+?)\s*\(₹?(\d+)\)$/);
        if (match) {
          tests.push({
            name: match[1].trim(),
            price: parseFloat(match[2])
          });
        } else {
          tests.push({
            name: testString,
            price: 0
          });
        }
      });
    } else if (order.serviceName) {
      // Single test
      tests.push({
        name: order.serviceName,
        price: order.totalAmount || 0
      });
    }
    
    // Fallback: check if order has items array (for multiple tests)
    if (tests.length === 0 && order.items && Array.isArray(order.items)) {
      order.items.forEach(item => {
        tests.push({
          name: item.testName || item.name || item.serviceName,
          price: item.price || item.amount || 0
        });
      });
    }
    
    // If still no tests, use default
    if (tests.length === 0) {
      tests.push({
        name: order.serviceName || 'Lab Test',
        price: order.totalAmount || 0
      });
    }
    
    return tests;
  };

  // Initialize samples based on tests in order
  const initializeSamples = () => {
    const tests = parseTestsFromOrder(order);
    
    return tests.map((test, index) => ({
      testName: test.name,
      testPrice: test.price,
      quantity: test.quantity || 1,
      sampleType: '',
      sampleQuality: 'good',
      vialNumber: '',
      collectionTime: new Date().toISOString().slice(0, 16),
      patientCondition: 'stable',
      fastingStatus: 'non-fasting',
      specialNotes: '',
      temperature: '',
      storageCondition: 'refrigerated',
      collectedBy: sessionStorage.getItem('userId') || '',
      sequenceNumber: index + 1,
      
      patientName: order.customerName || '',
      patientAge: order.patientAge || '',
      patientGender: order.patientGender || '',
      patientPhone: order.customerPhone || '',
    }));
  };

  const [samples, setSamples] = useState(initializeSamples());
  const [testsInfo, setTestsInfo] = useState(parseTestsFromOrder(order));

  useEffect(() => {
    // Reinitialize if order changes
    const tests = parseTestsFromOrder(order);
    setTestsInfo(tests);
    setSamples(initializeSamples());
  }, [order.id]);

  const sampleTypes = [
    'Blood - Serum',
    'Blood - Plasma',
    'Blood - Whole Blood',
    'Blood - EDTA',
    'Blood - Heparin',
    'Blood - Fluoride',
    'Urine - Random',
    'Urine - 24 Hour',
    'Urine - Early Morning',
    'Urine - Midstream',
    'Stool',
    'Saliva',
    'Sputum',
    'Swab - Throat',
    'Swab - Nasal',
    'Swab - Vaginal',
    'Tissue',
    'Biopsy',
    'CSF (Cerebrospinal Fluid)',
    'Other'
  ];

  const qualityOptions = [
    { value: 'excellent', label: 'Excellent', color: 'success' },
    { value: 'good', label: 'Good', color: 'success' },
    { value: 'acceptable', label: 'Acceptable', color: 'info' },
    { value: 'hemolyzed', label: 'Hemolyzed (Blood)', color: 'warning' },
    { value: 'lipemic', label: 'Lipemic (Blood)', color: 'warning' },
    { value: 'clotted', label: 'Clotted', color: 'error' },
    { value: 'insufficient', label: 'Insufficient Volume', color: 'error' },
    { value: 'contaminated', label: 'Contaminated', color: 'error' },
    { value: 'poor', label: 'Poor - Needs Recollection', color: 'error' }
  ];

  const patientConditions = [
    { value: 'stable', label: 'Stable' },
    { value: 'fasting', label: 'Fasting' },
    { value: 'non-fasting', label: 'Non-Fasting' },
    { value: 'post-meal', label: 'Post-Meal (PP)' },
    { value: 'pre-medication', label: 'Pre-Medication' },
    { value: 'post-medication', label: 'Post-Medication' },
    { value: 'anxious', label: 'Anxious/Stressed' },
    { value: 'other', label: 'Other' }
  ];

  const fastingStatusOptions = [
    { value: 'fasting', label: 'Fasting (8-12 hours)' },
    { value: 'non-fasting', label: 'Non-Fasting' },
    { value: 'partial-fasting', label: 'Partial Fasting (4-6 hours)' },
    { value: 'not-applicable', label: 'Not Applicable' }
  ];

  const storageOptions = [
    { value: 'room_temperature', label: 'Room Temperature (20-25°C)' },
    { value: 'refrigerated', label: 'Refrigerated (2-8°C)' },
    { value: 'frozen', label: 'Frozen (-20°C)' },
    { value: 'deep_frozen', label: 'Deep Frozen (-80°C)' },
    { value: 'dry_ice', label: 'Dry Ice Transport' }
  ];

  const handleSampleChange = (index, field, value) => {
    const newSamples = [...samples];
    newSamples[index] = {
      ...newSamples[index],
      [field]: value
    };
    setSamples(newSamples);
  };

  const addExtraSample = () => {
    setSamples([
      ...samples,
      {
        testName: 'Additional Sample',
        testPrice: 0,
        sampleType: '',
        sampleQuality: 'good',
        vialNumber: '',
        collectionTime: new Date().toISOString().slice(0, 16),
        patientCondition: 'stable',
        fastingStatus: 'non-fasting',
        specialNotes: '',
        temperature: '',
        storageCondition: 'refrigerated',
        collectedBy: sessionStorage.getItem('userId') || '',
        sequenceNumber: samples.length + 1,
           
      patientName: order.customerName || '',
      patientAge: order.patientAge || '',
      patientGender: order.patientGender || '',
      patientPhone: order.customerPhone || '',
      }
    ]);
  };

  const removeSample = (index) => {
    // Can only remove extra samples, not the original test samples
    if (index >= testsInfo.length && samples.length > testsInfo.length) {
      setSamples(samples.filter((_, i) => i !== index));
    }
  };

  const getQualityChipColor = (quality) => {
    const option = qualityOptions.find(opt => opt.value === quality);
    return option?.color || 'default';
  };

  const validateForm = () => {
    // Must collect samples for all tests
    const requiredSamplesCount = testsInfo.length;
    const filledSamples = samples.slice(0, requiredSamplesCount);
    
    for (let i = 0; i < filledSamples.length; i++) {
      const sample = filledSamples[i];
      
      if (!sample.testName) {
        setError(`Sample ${i + 1}: Test name is required`);
        return false;
      }
      if (!sample.sampleType) {
        setError(`Sample ${i + 1}: Sample type is required`);
        return false;
      }
      if (!sample.vialNumber) {
        setError(`Sample ${i + 1}: Vial/Container number is required`);
        return false;
      }
      if (!sample.collectionTime) {
        setError(`Sample ${i + 1}: Collection time is required`);
        return false;
      }

      // Check for poor quality
      if (['poor', 'insufficient', 'clotted', 'hemolyzed', 'contaminated'].includes(sample.sampleQuality)) {
        const testName = sample.testName || `Test ${i + 1}`;
        const confirmProceed = window.confirm(
          `⚠️ Sample for "${testName}" has quality issues (${sample.sampleQuality}).\n\n` +
          'This may affect test results or require recollection.\n\n' +
          'Do you want to proceed anyway?'
        );
        if (!confirmProceed) {
          return false;
        }
      }
    }
    return true;
  };

  const handleConfirmCollection = async () => {
    try {
      setError(null);
      
      if (!validateForm()) {
        return;
      }
      
      setLoading(true);
      
      console.log('Confirming sample collection:', {
        orderId: order.id,
        orderType: order.orderType,
        samples,
        testsCount: testsInfo.length
      });

      const response = await orderAssignmentAPI.completeSampleCollection(
        order.id,
        order.orderType,
        {
          samples: samples,
          totalSamples: samples.length,
          totalTests: testsInfo.length,
          collectedAt: new Date().toISOString(),
          collectedBy: sessionStorage.getItem('userId')
        }
      );

      if (response.data.success) {
        console.log('Sample collection confirmed:', response.data);
        
        setTimeout(() => {
          onSampleCollected(samples);
        }, 1000);
      } else {
        throw new Error(response.data.message || 'Failed to confirm collection');
      }
      
    } catch (err) {
      console.error('Sample collection error:', err);
      setError(err.response?.data?.message || err.message || 'Error confirming sample collection');
    } finally {
      setLoading(false);
    }
  };

  const collectionProgress = {
    completed: samples.filter(s => s.sampleType && s.vialNumber).length,
    total: testsInfo.length
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', pb: 4 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <IconButton onClick={onBack} disabled={loading}>
          <ArrowBack />
        </IconButton>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={600}>
            Sample Collection
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Order #{order.orderId} - {order.customerName}
          </Typography>
        </Box>
        <Chip 
          label={`${collectionProgress.completed}/${collectionProgress.total} Collected`}
          color={collectionProgress.completed === collectionProgress.total ? 'success' : 'warning'}
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Patient Info Summary */}
      <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Patient
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {order.customerName}
              </Typography>
              {order.patientAge && (
                <Typography variant="body2" color="text.secondary">
                  Age: {order.patientAge} years
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Tests
              </Typography>
              <Typography variant="h6" fontWeight={600} color="primary">
                {testsInfo.length} Test{testsInfo.length > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ₹{order.totalAmount}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Collection Status
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {collectionProgress.completed}/{collectionProgress.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Samples Collected
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tests Overview */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#e3f2fd' }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <Biotech color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Tests to Collect ({testsInfo.length})
          </Typography>
        </Stack>
        <Stack spacing={1}>
          {testsInfo.map((test, idx) => (
            <Stack 
              key={idx}
              direction="row" 
              justifyContent="space-between"
              alignItems="center"
              sx={{ 
                p: 1, 
                bgcolor: 'white', 
                borderRadius: 1,
                borderLeft: samples[idx]?.sampleType ? '4px solid #28a745' : '4px solid #ffc107'
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {idx + 1}. {test.name}
                </Typography>
                {samples[idx]?.sampleType && (
                  <Typography variant="caption" color="text.secondary">
                    {samples[idx].sampleType} - Vial: {samples[idx].vialNumber}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" fontWeight={600} color="primary">
                  ₹{test.price}
                </Typography>
                {samples[idx]?.sampleType ? (
                  <CheckCircle color="success" fontSize="small" />
                ) : (
                  <Warning color="warning" fontSize="small" />
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Paper>

      {/* Sample Collection Forms */}
      <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
        Sample Collection Details
      </Typography>

      <Stack spacing={2} mb={2}>
        {samples.map((sample, index) => {
          const isRequiredTest = index < testsInfo.length;
          const isCompleted = sample.sampleType && sample.vialNumber;
          
          return (
            <Accordion 
              key={index} 
              defaultExpanded={index === 0}
              sx={{
                border: '2px solid',
                borderColor: isCompleted ? '#28a745' : '#dee2e6',
                '&:before': { display: 'none' }
              }}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore />}
                sx={{
                  bgcolor: isCompleted ? '#d4edda' : '#fff3cd',
                  '&:hover': { bgcolor: isCompleted ? '#c3e6cb' : '#fff3cd' }
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} width="100%">
                  <Science color={isCompleted ? 'success' : 'warning'} />
                  <Box flexGrow={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {isRequiredTest ? '🔴 REQUIRED' : '➕ EXTRA'} Sample {index + 1}
                      </Typography>
                      {isCompleted && <CheckCircle color="success" fontSize="small" />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {sample.testName || 'Not specified'}
                      {sample.testPrice > 0 && ` (₹${sample.testPrice})`}
                    </Typography>
                    {sample.sampleType && (
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Chip 
                          label={sample.sampleType} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                        <Chip 
                          label={sample.sampleQuality} 
                          size="small" 
                          color={getQualityChipColor(sample.sampleQuality)}
                        />
                      </Stack>
                    )}
                  </Box>
                  {!isRequiredTest && (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSample(index);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              </AccordionSummary>
              
              <AccordionDetails sx={{ bgcolor: '#fafafa' }}>
                <Grid container spacing={2}>

                                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Patient Name"
                        value={sample.patientName}
                        onChange={(e) => handleSampleChange(index, 'patientName', e.target.value)}
                        disabled={loading}
                        sx={{ width: 400 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Patient Age"
                        value={sample.patientAge}
                        onChange={(e) => handleSampleChange(index, 'patientAge', e.target.value)}
                        disabled={loading}
                        sx={{ width: 400 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Patient Gender"
                        value={sample.patientGender}
                        onChange={(e) => handleSampleChange(index, 'patientGender', e.target.value)}
                        disabled={loading}
                        sx={{ width: 400 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Patient Phone"
                        value={sample.patientPhone}
                        onChange={(e) => handleSampleChange(index, 'patientPhone', e.target.value)}
                        disabled={loading}
                        sx={{ width: 400 }}
                      />
                    </Grid>

                  {/* Test Name */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Test Name *"
                      value={sample.testName}
                      onChange={(e) => handleSampleChange(index, 'testName', e.target.value)}
                      disabled={loading || (isRequiredTest && index < testsInfo.length)}
                      InputProps={{
                        readOnly: isRequiredTest && index < testsInfo.length
                      }}
                      sx={{ width: 400 }}
                    />
                  </Grid>


                  {/* Sample Type */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Sample Type"
                      value={sample.sampleType}
                      onChange={(e) => handleSampleChange(index, 'sampleType', e.target.value)}
                      disabled={loading}
                      required
                      sx={{ width: 400 }}
                      error={isRequiredTest && !sample.sampleType}
                      helperText={isRequiredTest && !sample.sampleType ? 'Required field' : ''}
                    >
                      {sampleTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Vial Number */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Vial/Container Number"
                      value={sample.vialNumber}
                      onChange={(e) => handleSampleChange(index, 'vialNumber', e.target.value)}
                      disabled={loading}
                      placeholder="e.g., V-2024-001234"
                      helperText="Unique barcode or ID"
                      required
                      error={isRequiredTest && !sample.vialNumber}
                      sx={{ width: 400 }} 
                    />
                  </Grid>

                  {/* Collection Time */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      sx={{ width: 400 }} 
                      type="datetime-local"
                      label="Collection Time *"
                      value={sample.collectionTime}
                      onChange={(e) => handleSampleChange(index, 'collectionTime', e.target.value)}
                      disabled={loading}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  {/* Sample Quality */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Sample Quality *"
                      value={sample.sampleQuality}
                      onChange={(e) => handleSampleChange(index, 'sampleQuality', e.target.value)}
                      disabled={loading}
                      sx={{ width: 400 }}
                    >
                      {qualityOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Patient Condition */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Patient Condition"
                      value={sample.patientCondition}
                      onChange={(e) => handleSampleChange(index, 'patientCondition', e.target.value)}
                      disabled={loading}
                      sx={{ width: 400 }}
                    >
                      {patientConditions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Fasting Status */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Fasting Status"
                      value={sample.fastingStatus}
                      onChange={(e) => handleSampleChange(index, 'fastingStatus', e.target.value)}
                      disabled={loading}
                      sx={{ width: 400 }}
                    >
                      {fastingStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Storage Condition */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Storage Condition *"
                      value={sample.storageCondition}
                      onChange={(e) => handleSampleChange(index, 'storageCondition', e.target.value)}
                      disabled={loading}
                      sx={{ width: 400 }}
                    >
                      {storageOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Temperature */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Storage Temperature (°C)"
                      value={sample.temperature}
                      onChange={(e) => handleSampleChange(index, 'temperature', e.target.value)}
                      disabled={loading}
                      placeholder="e.g., 4"
                      helperText="Actual measured temperature"
                      sx={{ width: 300 }}
                    />
                  </Grid>

                  {/* Special Notes */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={1}
                      label="Special Notes/Observations"
                      value={sample.specialNotes}
                      onChange={(e) => handleSampleChange(index, 'specialNotes', e.target.value)}
                      disabled={loading}
                      placeholder="Any special observations, patient concerns, or handling instructions..."
                      sx={{ width: 500 }}
                    />
                  </Grid>

                  {/* Quality Warning */}
                  {['poor', 'hemolyzed', 'lipemic', 'clotted', 'insufficient', 'contaminated'].includes(sample.sampleQuality) && (
                    <Grid item xs={12}>
                      <Alert severity="error" icon={<Warning />}>
                        <Typography variant="body2" fontWeight={600}>
                          ⚠️ Critical Sample Quality Issue
                        </Typography>
                        <Typography variant="body2">
                          This sample has serious quality concerns ({sample.sampleQuality}). 
                          {isRequiredTest && ' Recollection is strongly recommended.'}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Stack>

      {/* Add Extra Sample Button */}
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={addExtraSample}
        disabled={loading}
        fullWidth
        sx={{ mb: 3 }}
      >
        Add Extra Sample (Optional)
      </Button>

      {/* Collection Summary */}
      <Alert 
        severity={collectionProgress.completed === collectionProgress.total ? 'success' : 'warning'}
        sx={{ mb: 3 }}
      >
        <Typography variant="body2" fontWeight={600}>
          Collection Progress: {collectionProgress.completed}/{collectionProgress.total} Required Samples
        </Typography>
        {collectionProgress.completed < collectionProgress.total && (
          <Typography variant="body2">
            Please collect samples for all {testsInfo.length} tests before confirming.
          </Typography>
        )}
      </Alert>

      {/* Action Buttons */}
      <Stack spacing={2}>
        <Button
          variant="contained"
          color="success"
          size="large"
          fullWidth
          onClick={handleConfirmCollection}
          disabled={loading || collectionProgress.completed < collectionProgress.total}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
          sx={{
            py: 1.5,
            bgcolor: '#28a745',
            '&:hover': { bgcolor: '#218838' }
          }}
        >
          {loading 
            ? 'Confirming...' 
            : `CONFIRM COLLECTION (${samples.length} Sample${samples.length > 1 ? 's' : ''} - ${testsInfo.length} Test${testsInfo.length > 1 ? 's' : ''})`
          }
        </Button>

        <Button
          variant="outlined"
          color="warning"
          size="large"
          fullWidth
          onClick={onReportFailure}
          disabled={loading}
          startIcon={<Warning />}
        >
          Report Collection Failure
        </Button>

        <Button
          variant="outlined"
          size="large"
          fullWidth
          onClick={onBack}
          disabled={loading}
        >
          Cancel
        </Button>
      </Stack>

      {/* Help Text */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
        📋 All {testsInfo.length} required test samples must be collected before confirming.
        <br />
        Sample quality directly affects test reliability. Ensure proper collection and storage.
      </Typography>
    </Box>
  );
};

export default SampleCollectionForm;